import { useEffect, useId, useMemo, useRef, useState, type ComponentType } from 'react';
import { Check, Copy, GitPullRequest, Globe, Inbox, MessageCircleQuestion, MonitorCheck, PenLine } from 'lucide-react';
import { Button } from '@vd/ui';
import { MAX_COLUMNS } from '../../routes/settings/columnLimit';
import { AGENT_INSTRUCTIONS_MAX_CHARS, type AgentInstructions, type Column } from '@vd/types';
import { formatWhen } from '../../lib/time';
import { useCopyFeedback } from '../../lib/useCopyFeedback';
import { ColumnDot } from '../board/ColumnDot';
import { buildAgentInstructions, readColumnNote, suggestedColumns, withColumnNote, WORKFLOW_EXAMPLES, type WorkflowExample } from './recommendation';
import styles from './BoardWorkflow.module.css';

/**
 * How this board works, as a project settings page (TOADO-972; moved off the
 * board's question mark into settings, Jason, 28 Oct, TOADO-987): what its
 * columns mean to Toado, and the project's instructions for agents (TOADO-998),
 * one text the team saves and get_ticket_brief hands to every agent, so nobody
 * copies it and an agent always reads the latest. An example fills it in, written
 * with the board's own names.
 *
 * Only two things on a board mean something to Toado itself, both flags on a
 * column: Bug intake and waiting on the reporter. Everything else, In Review
 * and Done included, means what the team says it does, so this page offers
 * examples and leaves the answers to them; an answer left empty stays visibly
 * open in the instruction rather than being filled with a default.
 */

interface BoardWorkflowProps {
  projectName: string;
  columns: readonly Column[];
  /** The project's Bug intake setting. */
  intakeOn: boolean;
  /** To the Columns settings, where the two flags are set. */
  onEditColumns: () => void;
  /**
   * Giving the board a column that waits on the reporter, where it has none
   * (TOADO-970). Left out for someone who cannot change the board's columns:
   * they are told who can.
   */
  waitingStage?: {
    add: () => Promise<void>;
    mark: (columnId: string) => Promise<void>;
    /**
     * Column changes not saved yet in Columns (review round 3): saving that
     * draft after this would undo it, so this waits for it, as Bug intake does.
     */
    /**
     * Why a change waits: column edits not saved yet, a columns save still out (review round 6),
     * or Bug intake being switched, which rewrites the board too (review round 16).
     */
    blocked?: 'unsaved' | 'saving' | 'intake';
  };
  /**
   * The project's instructions for agents (TOADO-998): what is saved, whether this
   * person can change it, the save, and this person's draft. The draft is the
   * settings page's (review round 1), so switching tabs keeps it; null is not
   * editing, when the box shows the saved text, a teammate's save included.
   */
  instructions: {
    saved: AgentInstructions | null;
    canEdit: boolean;
    save: (text: string | null) => Promise<AgentInstructions | null>;
    /**
     * A save is out (review round 3): the page's, like the draft, so a tab switch keeps
     * the box waiting for it rather than mounting it afresh as free to edit.
     */
    saving: boolean;
    draft: string | null;
    /** Takes a value, or a function of the draft as it is now (a React state setter). */
    onDraft: (next: string | null | ((current: string | null) => string | null)) => void;
  };
}

/**
 * What answering does, said where the stage is described (TOADO-970): nothing
 * moves a ticket out of the waiting column, or tells an agent, on its own.
 */
function AnsweringNote({ columnName }: { columnName: string }) {
  return (
    <p className={styles.muted} data-testid="workflow-answering-note">
      When an agent asks the person who reported a ticket a question, the ticket moves to {columnName} and waits under their
      Needs you on the Dashboard. Answering in a comment does not move it back or tell the agent by itself: answer, then move
      the ticket to the column work goes on from, or ask the agent to pick it up again.
    </p>
  );
}

/**
 * A board with no column waiting on the reporter (TOADO-970): a board made
 * before the stage, or one a team changed. Whoever can change its columns can
 * add one or mark one of theirs; nothing is changed for them, and anyone else
 * is told who can.
 */
function NoWaitingStage({ columns, controls }: { columns: readonly Column[]; controls: BoardWorkflowProps['waitingStage'] }) {
  const id = useId();
  // Not the intake column. A done column is refused by the server, which says why: the board here does not know which one it is.
  const markable = columns.filter((column) => !column.isIntake);
  const [choice, setChoice] = useState('');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const full = columns.length >= MAX_COLUMNS;
  const run = async (action: () => Promise<void>) => {
    setBusy(true);
    setProblem(null);
    try {
      await action();
    } catch (error) {
      setProblem(error instanceof Error ? error.message : 'The board could not be changed.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className={styles.waitingMissing} role="region" aria-labelledby={`${id}-title`} data-testid="workflow-no-waiting-stage">
      <strong id={`${id}-title`}>No column waits on the reporter</strong>
      <p className={styles.muted}>
        When an agent asks the person who reported a ticket a question, it moves the ticket to that column, so the board shows
        it waits on them and their Dashboard lists it under Needs you. This board has none, so the question is posted and the
        ticket stays where it is.
      </p>
      {controls?.blocked === 'saving' ? (
        <p className={styles.muted} data-testid="workflow-waiting-blocked">
          Your columns are being saved. Add or mark a column once that is done.
        </p>
      ) : controls?.blocked === 'intake' ? (
        <p className={styles.muted} data-testid="workflow-waiting-blocked">
          Bug intake is being switched. Add or mark a column once that is done.
        </p>
      ) : controls?.blocked ? (
        <p className={styles.muted} data-testid="workflow-waiting-blocked">
          Your column changes in Columns are not saved yet. Adding or marking a column changes the columns, so save or
          discard them there first.
        </p>
      ) : controls ? (
        <div className={styles.waitingActions}>
          <Button size="sm" onClick={() => run(controls.add)} disabled={busy || full}>
            Add a Needs reply column
          </Button>
          {markable.length > 0 && (
            <div className={styles.waitingMark}>
              <label htmlFor={`${id}-mark`}>Or mark one of yours</label>
              <select id={`${id}-mark`} value={choice} onChange={(event) => setChoice(event.target.value)} disabled={busy}>
                <option value="">Choose a column</option>
                {markable.map((column) => <option key={column.id} value={column.id}>{column.name}</option>)}
              </select>
              <Button size="sm" variant="secondary" onClick={() => run(() => controls.mark(choice))} disabled={busy || !choice}>
                Mark it
              </Button>
            </div>
          )}
          {full && <p className={styles.muted}>This board has {MAX_COLUMNS} columns, the most it can, so mark one instead.</p>}
          {problem && <p className={styles.problem} role="alert">{problem}</p>}
        </div>
      ) : (
        <p className={styles.muted}>A project member can add one here, or mark one of the board&apos;s columns in Columns.</p>
      )}
    </div>
  );
}

/** A picture for each example (Jason, 30 Oct, t-BJCndr): where its review happens. */
const EXAMPLE_ICONS: Record<WorkflowExample['key'], ComponentType<{ size?: number; 'aria-hidden'?: boolean }>> = {
  'code-review': GitPullRequest,
  'preview-qa': MonitorCheck,
  'live-acceptance': Globe,
  custom: PenLine,
};

/** How long "Saved" shows beside the button after a save. */
const SAVED_SHOWN_MS = 2_500;

/** Who saved the instructions and when, in the person's own zone. */
function savedLine(saved: AgentInstructions): string {
  return `Saved${saved.updatedBy?.name ? ` by ${saved.updatedBy.name}` : ''}, ${formatWhen(saved.updatedAt, { exact: true })}.`;
}

export function BoardWorkflow({ projectName, columns, intakeOn, onEditColumns, waitingStage, instructions }: BoardWorkflowProps) {
  const id = useId();
  const saved = instructions.saved;
  // Without the right to edit there is no draft (review round 2): what is shown and copied is the saved text.
  const text = (instructions.canEdit ? instructions.draft : null) ?? saved?.text ?? '';
  const setText = instructions.onDraft;
  // A draft that has become exactly what is saved is no draft (review round 1), so a later save
  // elsewhere shows. Exact, not trimmed: a trailing space being typed never resets the box.
  // Nothing is typed while a save is out (the box waits, review round 3), so this needs no wait of its own.
  const { draft, onDraft, saving } = instructions;
  useEffect(() => {
    if (draft !== null && draft === (saved?.text ?? '')) onDraft(null);
  }, [draft, saved?.text, onDraft]);
  /** An example asked for while something is written: replacing it waits for a yes. */
  const [pendingExample, setPendingExample] = useState<WorkflowExample | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [copied, copy] = useCopyFeedback();
  /** The example last put in the box here, kept while the text is edited from it. */
  const [appliedKey, setAppliedKey] = useState<WorkflowExample['key'] | null>(null);
  const [savedShown, setSavedShown] = useState(false);
  const editorRef = useRef<HTMLTextAreaElement>(null);
  /** Where the caret goes once the box shows a column's line. */
  const pendingCaret = useRef<number | null>(null);
  useEffect(() => {
    if (!savedShown) return undefined;
    const timer = setTimeout(() => setSavedShown(false), SAVED_SHOWN_MS);
    return () => clearTimeout(timer);
  }, [savedShown]);
  // After the render that shows a column's line: the caret at its end, ready to type.
  useEffect(() => {
    const caret = pendingCaret.current;
    const editor = editorRef.current;
    if (caret === null || !editor) return;
    pendingCaret.current = null;
    editor.focus();
    editor.setSelectionRange(caret, caret);
  });

  const unchanged = text.trim() === (saved?.text ?? '').trim();
  const length = text.trim().length;
  const tooLong = length > AGENT_INSTRUCTIONS_MAX_CHARS;

  const exampleText = useMemo(() => {
    const board = columns.map((column) => ({
      id: column.id, name: column.name, isIntake: column.isIntake, waitingOnReporter: column.waitingOnReporter, isTerminal: column.isTerminal,
    }));
    // Filled in from the board's flags and order, never a name (t-Zw5xUW; TOADO-972 never guessed from a name).
    const picked = suggestedColumns(board);
    return new Map(WORKFLOW_EXAMPLES.map((example) => [example.key, buildAgentInstructions({
      projectName, columns: board, intakeOn, ...picked, answers: { ...example.answers },
    })] as const));
  }, [projectName, columns, intakeOn]);
  // Which example the box holds (t-kF6Der): one whose exact text it is, a saved one included,
  // else the one last used here and edited since.
  const selectedKey = WORKFLOW_EXAMPLES.find((example) => exampleText.get(example.key) === text)?.key ?? appliedKey;

  const fillFrom = (example: WorkflowExample) => {
    // Nothing changes the text while a save is out (review round 4: Replace it could).
    if (saving) return;
    setPendingExample(null);
    setProblem(null);
    setAppliedKey(example.key);
    setText(exampleText.get(example.key) ?? '');
  };

  /** A column pressed on the small board (t-kF6Der): its line in the instructions, ready to finish. */
  const editColumnNote = (column: Column) => {
    if (saving) return;
    setProblem(null);
    const next = withColumnNote(text, column.name);
    pendingCaret.current = next.caret;
    if (next.text !== text) setText(next.text);
    else if (editorRef.current) {
      pendingCaret.current = null;
      editorRef.current.focus();
      editorRef.current.setSelectionRange(next.caret, next.caret);
    }
  };
  const chooseExample = (example: WorkflowExample) => {
    if (text.trim()) setPendingExample(example);
    else fillFrom(example);
  };

  const save = async () => {
    setProblem(null);
    try {
      const sent = text;
      await instructions.save(sent.trim() || null);
      setSavedShown(true);
      // Saved: the box is the saved text again, as the server kept it. Only the draft that was
      // sent is cleared (review round 2): one typed after a tab switch while this was out stays.
      setText((current) => (current === sent ? null : current));
    } catch (error) {
      setProblem(error instanceof Error && error.message ? error.message : 'The instructions could not be saved. Try again.');
    }
  };

  const flagged = columns.some((column) => column.isIntake || column.waitingOnReporter);
  const waiting = columns.find((column) => column.waitingOnReporter);

  return (
    <div className={styles.page}>
      <header className={styles.intro}>
        <h2 className={styles.title}>How this board works</h2>
        <p className={styles.muted}>
          Tell agents how your team moves tickets on this board. Agents connected to Toado read it whenever they open a
          ticket in this project, so nobody has to copy it to them.
        </p>
      </header>

      <section className={styles.section} aria-labelledby={`${id}-columns`}>
        <div className={styles.sectionHead}>
          <h3 id={`${id}-columns`}>What Toado reads from these columns</h3>
          <Button size="sm" variant="secondary" onClick={onEditColumns}>Edit columns</Button>
        </div>
        <p className={styles.muted}>
          Toado reads two settings on a column and nothing else: not a column&apos;s name, and not where it sits.
          What In Review or Done means is your team&apos;s to say, below{instructions.canEdit ? ': press a column to write what happens there' : ''}.
        </p>
        {/* The board, drawn small, with the two flags Toado reads marked on it. */}
        <ol className={styles.miniBoard} aria-label={`${projectName}'s columns`}>
          {columns.map((column) => {
            const intake = column.isIntake;
            const waitingHere = column.waitingOnReporter;
            const note = readColumnNote(text, column.name);
            const body = (
              <>
                <span className={styles.miniName}><ColumnDot tone={column.tone} /> {column.name}</span>
                {intake && (
                  <span className={styles.flag}>
                    <Inbox size={12} aria-hidden />
                    {intakeOn ? 'Bug intake: outside reports land here' : 'Marked for Bug intake, which is off'}
                  </span>
                )}
                {waitingHere && (
                  <span className={styles.flag}>
                    <MessageCircleQuestion size={12} aria-hidden /> Waiting on the reporter
                  </span>
                )}
                {note
                  ? <span className={styles.miniNote}>{note}</span>
                  : instructions.canEdit && <span className={styles.miniHint}>Write what happens here</span>}
              </>
            );
            // Pressing a column writes its line below (t-kF6Der); without the right to edit it only shows.
            return (
              <li key={column.id} className={styles.miniColumn}>
                {instructions.canEdit
                  ? <button type="button" className={styles.miniBody} onClick={() => editColumnNote(column)} disabled={saving}>{body}</button>
                  : <div className={styles.miniBody}>{body}</div>}
              </li>
            );
          })}
        </ol>
        {!flagged && (
          <p className={styles.muted}>No column here is marked for Bug intake or for waiting on the reporter. Mark them in Columns.</p>
        )}
        {waiting
          ? <AnsweringNote columnName={waiting.name} />
          : <NoWaitingStage columns={columns} controls={waitingStage} />}
      </section>

      {/* The team's word to agents (TOADO-998): saved for the project, handed over by get_ticket_brief. */}
      <section className={styles.section} aria-labelledby={`${id}-agent`} data-testid="agent-instructions">
        <h3 id={`${id}-agent`}>Instructions for agents</h3>
        <p className={styles.muted}>
          When it reviews, what counts as done, and whether it may deploy. Start from an example, then make it say what
          your team does.
        </p>
        <p className={styles.muted} data-testid="agent-instructions-state">
          {saved ? savedLine(saved) : 'Nothing saved yet, so agents get no instructions for this project.'}
          {!unchanged && instructions.canEdit ? ' You have changes here that are not saved.' : ''}
        </p>
        {instructions.canEdit && (
          <div className={styles.examples} role="group" aria-label="Start from an example">
            {WORKFLOW_EXAMPLES.map((example) => {
              const Icon = EXAMPLE_ICONS[example.key];
              const selected = selectedKey === example.key;
              // A card that reads as a button (t-BJCndr): a picture, the choice, and what pressing it does.
              return (
                <button
                  key={example.key}
                  type="button"
                  className={styles.example}
                  onClick={() => chooseExample(example)}
                  disabled={saving}
                  aria-pressed={selected}
                >
                  <span className={styles.exampleIcon}><Icon size={18} aria-hidden /></span>
                  <strong>{example.title}</strong>
                  <span className={styles.exampleSummary}>{example.summary}</span>
                  <span className={styles.exampleAction} aria-hidden>
                    {selected ? <><Check size={14} aria-hidden /> In use</> : 'Use this'}
                  </span>
                </button>
              );
            })}
          </div>
        )}
        {pendingExample && (
          <div className={styles.confirm} role="group" aria-label="Replace the instructions">
            <span>Replace what is written here with &ldquo;{pendingExample.title}&rdquo;?</span>
            <Button size="sm" variant="primary" onClick={() => fillFrom(pendingExample)} disabled={saving}>Replace it</Button>
            <Button size="sm" variant="secondary" onClick={() => setPendingExample(null)} disabled={saving}>Keep mine</Button>
          </div>
        )}
        <div className={styles.editorHead}>
          <h4 className={styles.editorTitle} id={`${id}-editor-title`}>What your agents read</h4>
          <p className={styles.muted}>
            Agents connected to Toado read this on every ticket in this project.
            {instructions.canEdit ? ' It is yours to edit: change any line, and answer each [not decided yet].' : ''}
          </p>
        </div>
        <textarea
          ref={editorRef}
          aria-describedby={`${id}-editor-title`}
          className={styles.editor}
          aria-label="What agents read for this project"
          rows={14}
          value={text}
          // Not while a save is out (review round 1): what is typed then would be lost when it lands.
          readOnly={!instructions.canEdit || saving}
          onChange={(event) => { setText(event.target.value); setProblem(null); }}
          placeholder={instructions.canEdit ? 'Start from an example above, or write how your team works.' : ''}
        />
        {/* Copy first and larger, Save at the far end with its confirmation (t-kF6Der). */}
        <div className={styles.saveRow}>
          <div className={styles.saveSide}>
            <Button size="md" variant="secondary" leftIcon={<Copy size={16} aria-hidden />} onClick={() => { void copy(text); }} disabled={!text.trim()}>
              {copied === 'yes' ? 'Copied' : 'Copy'}
            </Button>
            <span className={tooLong ? styles.problem : styles.muted} data-testid="agent-instructions-count">
              {length.toLocaleString()} of {AGENT_INSTRUCTIONS_MAX_CHARS.toLocaleString()} characters
            </span>
          </div>
          {instructions.canEdit && (
            <div className={styles.saveSide}>
              <span className={styles.savedNote} role="status" data-testid="agent-instructions-saved">
                {savedShown && unchanged && <><Check size={14} aria-hidden /> Saved</>}
              </span>
              <Button size="md" variant="primary" onClick={() => { void save(); }} disabled={saving || unchanged || tooLong}>
                {saving ? 'Saving…' : 'Save'}
              </Button>
            </div>
          )}
        </div>
        {copied === 'failed' && <p className={styles.problem} role="status">Could not copy: select the text and copy it yourself.</p>}
        {!instructions.canEdit && <p className={styles.muted}>A project member can change these.</p>}
        {problem && <p className={styles.problem} role="alert">{problem}</p>}
        <p className={styles.muted}>
          For an agent not connected to Toado, copy them into its project instructions instead. On a ticket, Fixes lists
          what an agent recorded doing, and its notes are in the comments.
        </p>
      </section>
    </div>
  );
}
